import { initReactI18next, useTranslation } from "react-i18next";
import i18next, { TFunction } from "i18next";
import DE from "@/core/locales/de/default.json";
import DE_ERROR_CODE from "@/core/locales/de/error-code.json";
import DE_FIELD_NAME from "@/core/locales/de/field-name.json";
import DE_API from "@/core/locales/de/api.json";
import {Language} from "@/core/api/model/enums.ts";

export enum LOCALE_NS {
    DEFAULT = "default",
    ERROR_CODE = "errorCode",
    FIELD_NAME = "fieldName",
    API = "api",
}

export type DefaultTranslation = TFunction<LOCALE_NS.DEFAULT>;
export type ErrorCodeTranslation = TFunction<LOCALE_NS.ERROR_CODE>;
export type FieldNameTranslation = TFunction<LOCALE_NS.FIELD_NAME>;
export type ApiTranslation = TFunction<LOCALE_NS.API>;

export type FieldNameKey = keyof typeof DE_FIELD_NAME;

export interface ErrorTranslation {
    errorCodes: ErrorCodeTranslation;
    fieldNames: FieldNameTranslation;
}

const resources = {
    [Language.de]: {
        [LOCALE_NS.DEFAULT]: DE,
        [LOCALE_NS.ERROR_CODE]: DE_ERROR_CODE,
        [LOCALE_NS.FIELD_NAME]: DE_FIELD_NAME,
        [LOCALE_NS.API]: DE_API,
    },
} as const;

/**
 * Initialized i18n instance
 * @see: https://www.i18next.com/overview/configuration-options
 */
export function initI18n(lang = Language.de) {
    return i18next.use(initReactI18next).init({
        resources,

        lng: lang, // if you're using a language detector, do not define the lng option
        fallbackLng: lang,

        defaultNS: LOCALE_NS.DEFAULT,
        ns: [LOCALE_NS.DEFAULT, LOCALE_NS.ERROR_CODE, LOCALE_NS.FIELD_NAME, LOCALE_NS.API],

        interpolation: {
            escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
        },
    });
}

/**
 * Custom hook to use translations for api namespace
 */
export function useApiTranslation() {
    return useTranslation(LOCALE_NS.API);
}

export function useErrorTranslation(): ErrorTranslation {
    const { t: errorCodeTranslation } = useTranslation(LOCALE_NS.ERROR_CODE);
    const { t: fieldNameTranslation } = useTranslation(LOCALE_NS.FIELD_NAME);
    return { errorCodes: errorCodeTranslation, fieldNames: fieldNameTranslation };
}

// re-export function for consistent usage
export { useTranslation } from "react-i18next";
